/**
 * Bot Filters Component
 *
 * Filter panel for trading bots with type, status, and symbol search
 */

import { useState } from 'react';
import { Icons } from '../Icons';
import type { TradingBot } from '../../lib/api/cryptocom-api';

interface BotFiltersProps {
  bots: TradingBot[];
  onFilterChange: (filteredBots: TradingBot[]) => void;
}

type BotType = TradingBot['type'] | 'ALL';
type BotStatus = TradingBot['status'] | 'ALL';

export function BotFilters({ bots, onFilterChange }: BotFiltersProps) {
  const [selectedType, setSelectedType] = useState<BotType>('ALL');
  const [selectedStatus, setSelectedStatus] = useState<BotStatus>('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Apply filters whenever they change
  const applyFilters = (type: BotType, status: BotStatus, search: string) => {
    let filtered = [...bots];

    // Filter by type
    if (type !== 'ALL') {
      filtered = filtered.filter(bot => bot.type === type);
    }

    // Filter by status
    if (status !== 'ALL') {
      filtered = filtered.filter(bot => bot.status === status);
    }

    // Filter by symbol search
    if (search.trim()) {
      const searchLower = search.toLowerCase().trim();
      filtered = filtered.filter(bot =>
        bot.symbol.toLowerCase().includes(searchLower)
      );
    }

    onFilterChange(filtered);
  };

  const handleTypeChange = (type: BotType) => {
    setSelectedType(type);
    applyFilters(type, selectedStatus, searchTerm);
  };

  const handleStatusChange = (status: BotStatus) => {
    setSelectedStatus(status);
    applyFilters(selectedType, status, searchTerm);
  };

  const handleSearchChange = (search: string) => {
    setSearchTerm(search);
    applyFilters(selectedType, selectedStatus, search);
  };

  const handleReset = () => {
    setSelectedType('ALL');
    setSelectedStatus('ALL');
    setSearchTerm('');
    onFilterChange(bots);
  };

  const hasActiveFilters = selectedType !== 'ALL' || selectedStatus !== 'ALL' || searchTerm.trim() !== '';

  const botTypes: { value: BotType; label: string; color: string }[] = [
    { value: 'ALL', label: 'Tous les types', color: '#888' },
    { value: 'GRID', label: 'Grid', color: '#3b82f6' },
    { value: 'DCA', label: 'DCA', color: '#10b981' },
    { value: 'TWAP', label: 'TWAP', color: '#f59e0b' },
    { value: 'FUNDING_ARBITRAGE', label: 'Arbitrage', color: '#8b5cf6' },
  ];

  const botStatuses: { value: BotStatus; label: string }[] = [
    { value: 'ALL', label: 'Tous les status' },
    { value: 'ACTIVE', label: 'Actifs' },
    { value: 'STOPPED', label: 'Arrêtés' },
    { value: 'COMPLETED', label: 'Terminés' },
  ];

  return (
    <div style={{ marginBottom: '16px' }}>
      {/* Filter Toggle Button */}
      <button
        onClick={() => setShowFilters(!showFilters)}
        style={{
          width: '100%',
          padding: '12px 16px',
          background: 'var(--bg)',
          border: '1px solid var(--border)',
          borderRadius: '8px',
          color: 'var(--text)',
          fontSize: '14px',
          fontWeight: 500,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: showFilters ? '12px' : '0'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icons.Filter size={16} />
          <span>Filtres</span>
          {hasActiveFilters && (
            <span style={{
              padding: '2px 8px',
              background: 'var(--accent)',
              color: 'white',
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: 600
            }}>
              {[selectedType !== 'ALL', selectedStatus !== 'ALL', searchTerm.trim()].filter(Boolean).length}
            </span>
          )}
        </div>
        <Icons.ChevronDown
          size={16}
          style={{
            transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s'
          }}
        />
      </button>

      {/* Filter Panel */}
      {showFilters && (
        <div style={{
          padding: '16px',
          background: 'var(--bg)',
          border: '1px solid var(--border)',
          borderRadius: '8px'
        }}>
          {/* Search by Symbol */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: 500,
              marginBottom: '8px',
              color: 'var(--muted)'
            }}>
              Rechercher par symbole
            </label>
            <div style={{ position: 'relative' }}>
              <Icons.Search
                size={16}
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--muted)'
                }}
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="BTC, ETH, CRO..."
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 36px',
                  background: 'var(--card)',
                  border: '1px solid var(--border)',
                  borderRadius: '6px',
                  color: 'var(--text)',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>

          {/* Filter by Type */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: 500,
              marginBottom: '8px',
              color: 'var(--muted)'
            }}>
              Type de bot
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
              gap: '8px'
            }}>
              {botTypes.map(type => (
                <button
                  key={type.value}
                  onClick={() => handleTypeChange(type.value)}
                  style={{
                    padding: '8px 12px',
                    background: selectedType === type.value ? type.color : 'var(--card)',
                    border: `1px solid ${selectedType === type.value ? type.color : 'var(--border)'}`,
                    borderRadius: '6px',
                    color: selectedType === type.value ? 'white' : 'var(--text)',
                    fontSize: '12px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Filter by Status */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: 500,
              marginBottom: '8px',
              color: 'var(--muted)'
            }}>
              Status
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
              gap: '8px'
            }}>
              {botStatuses.map(status => (
                <button
                  key={status.value}
                  onClick={() => handleStatusChange(status.value)}
                  style={{
                    padding: '8px 12px',
                    background: selectedStatus === status.value ? 'var(--accent)' : 'var(--card)',
                    border: `1px solid ${selectedStatus === status.value ? 'var(--accent)' : 'var(--border)'}`,
                    borderRadius: '6px',
                    color: selectedStatus === status.value ? 'white' : 'var(--text)',
                    fontSize: '12px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {status.label}
                </button>
              ))}
            </div>
          </div>

          {/* Reset Filters */}
          {hasActiveFilters && (
            <button
              onClick={handleReset}
              style={{
                width: '100%',
                padding: '10px 12px',
                background: 'transparent',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                color: 'var(--muted)',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = 'var(--accent)';
                e.currentTarget.style.color = 'var(--accent)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.color = 'var(--muted)';
              }}
            >
              <Icons.X size={14} />
              Réinitialiser les filtres
            </button>
          )}
        </div>
      )}
    </div>
  );
}
